import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShip,
  faExclamationTriangle,
  faCheckCircle,
  faClock,
  faCalendarAlt,
  faInfoCircle,
  faArrowRight,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";

const DashboardSummaryCards = ({
  metrics,
  data,
  onShowOutOfTimeModal,
  onShowValidationProcessModal,
  onShowEtaComplianceModal,
  onShowProcessingTimeModal,
}) => {
  const {
    totalReferences,
    etaComplianceCount,
    validationProcessCount,
    averageProcessingTime,
    outOfTimeReferences,
  } = metrics;

  // Calculate percentages for visual indicators
  const etaCompliancePercentage = totalReferences
    ? Math.round((etaComplianceCount / totalReferences) * 100)
    : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
      {/* Total References */}
      <div className="bg-white rounded-lg shadow p-4 border border-gray-200 transition-all duration-200 hover:shadow-md">
        <div className="flex items-start">
          <div className="bg-blue-100 p-3 rounded-full">
            <FontAwesomeIcon icon={faShip} className="text-blue-600" />
          </div>
          <div className="ml-4">
            <h3 className="text-sm font-medium text-gray-500">
              Total References
            </h3>
            <p className="text-2xl font-bold text-gray-900">
              {totalReferences}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Active shipments in the system
            </p>
          </div>
        </div>
      </div>

      {/* ETA Compliance */}
      <div
        className="bg-white rounded-lg shadow p-4 border border-gray-200 transition-all duration-200 hover:shadow-md hover:border-blue-200 cursor-pointer"
        onClick={() => onShowEtaComplianceModal(false)}
      >
        <div className="flex items-start">
          <div
            className={`p-3 rounded-full ${
              etaCompliancePercentage > 80 ? "bg-green-100" : "bg-red-100"
            }`}
          >
            <FontAwesomeIcon
              icon={
                etaCompliancePercentage > 80 ? faCheckCircle : faTimesCircle
              }
              className={
                etaCompliancePercentage > 80 ? "text-green-600" : "text-red-600"
              }
            />
          </div>
          <div className="ml-4 flex-grow">
            <div className="flex justify-between items-start">
              <h3 className="text-sm font-medium text-gray-500">
                ETA Compliance
              </h3>
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="text-blue-500 text-xs"
                title="References with ETA date within 7 days of Alta date"
              />
            </div>
            <div className="flex items-baseline">
              <p className="text-2xl font-bold text-gray-900">
                {etaCompliancePercentage}%
              </p>
              <span className="ml-2 text-sm text-gray-600">
                ({etaComplianceCount}/{totalReferences})
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
              <div
                className={`h-1.5 rounded-full ${
                  etaCompliancePercentage > 80
                    ? "bg-green-500"
                    : etaCompliancePercentage > 50
                    ? "bg-yellow-500"
                    : "bg-red-500"
                }`}
                style={{ width: `${etaCompliancePercentage}%` }}
              ></div>
            </div>
            <div className="mt-2 text-xs text-blue-600 font-medium flex items-center">
              <span>View non-compliant</span>
              <FontAwesomeIcon icon={faArrowRight} className="ml-1" />
            </div>
          </div>
        </div>
      </div>

      {/* References in Validation */}
      <div
        className="bg-white rounded-lg shadow p-4 border border-gray-200 transition-all duration-200 hover:shadow-md hover:border-blue-200 cursor-pointer"
        onClick={onShowValidationProcessModal}
      >
        <div className="flex items-start">
          <div className="bg-indigo-100 p-3 rounded-full">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-indigo-600" />
          </div>
          <div className="ml-4 flex-grow">
            <div className="flex justify-between items-start">
              <h3 className="text-sm font-medium text-gray-500">
                In Validation Process
              </h3>
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="text-blue-500 text-xs"
                title="References within 3-day validation window"
              />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {validationProcessCount}
            </p>
            <p className="text-xs text-gray-500 mt-1">Within 3-day window</p>
            <div className="mt-2 text-xs text-blue-600 font-medium flex items-center">
              <span>View references</span>
              <FontAwesomeIcon icon={faArrowRight} className="ml-1" />
            </div>
          </div>
        </div>
      </div>

      {/* Out of Time References */}
      <div
        className="bg-white rounded-lg shadow p-4 border border-gray-200 transition-all duration-200 hover:shadow-md hover:border-blue-200 cursor-pointer"
        onClick={onShowOutOfTimeModal}
      >
        <div className="flex items-start">
          <div className="bg-red-100 p-3 rounded-full">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="text-red-600"
            />
          </div>
          <div className="ml-4 flex-grow">
            <div className="flex justify-between items-start">
              <h3 className="text-sm font-medium text-gray-500">Out of Time</h3>
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="text-blue-500 text-xs"
                title="References that exceeded 3-day validation window"
              />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {outOfTimeReferences}
            </p>
            {outOfTimeReferences > 0 && (
              <p className="text-xs text-red-600 mt-1">
                Needs immediate attention
              </p>
            )}
            <div className="mt-2 text-xs text-blue-600 font-medium flex items-center">
              <span>View references</span>
              <FontAwesomeIcon icon={faArrowRight} className="ml-1" />
            </div>
          </div>
        </div>
      </div>

      {/* Average Processing Time */}
      <div
        className="bg-white rounded-lg shadow p-4 border border-gray-200 transition-all duration-200 hover:shadow-md hover:border-blue-200 cursor-pointer"
        onClick={() => onShowProcessingTimeModal("exit")}
      >
        <div className="flex items-start">
          <div className="bg-yellow-100 p-3 rounded-full">
            <FontAwesomeIcon icon={faClock} className="text-yellow-600" />
          </div>
          <div className="ml-4 flex-grow">
            <div className="flex justify-between items-start">
              <h3 className="text-sm font-medium text-gray-500">
                Avg. Processing
              </h3>
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="text-blue-500 text-xs"
                title="Average processing time across all references"
              />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {averageProcessingTime > 0
                ? `${averageProcessingTime} days`
                : "N/A"}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              From arrival to completion
            </p>
            <div className="mt-2 text-xs text-blue-600 font-medium flex items-center">
              <span>View time analysis</span>
              <FontAwesomeIcon icon={faArrowRight} className="ml-1" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardSummaryCards;
